import clsx from 'clsx'
import FleetStats from 'components/FleetStats/FleetStats'
import Loader from 'components/Loader/Loader'
import TablePagination from 'components/TablePagination/TablePagination'
import RobotItem from 'pages/Assets/robots/RobotItem/RobotItem'
import { KeyboardEvent, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { PROGRESS_TYPES, ROBOT_NETWORK_STATUSES } from 'utils/constants'
import { IRobotInfo, IStation } from 'utils/interfaces'
import { hasActiveTaskRuntimeState } from 'utils/taskRuntimeState'
import { sortWithFavoritesFirst } from 'utils/favoritesSort'
import { useUser } from 'hooks/user'

import robotStyle from 'pages/Assets/robots/AssetsRobotsTable.module.css'

export type AssetsRobotsPanelProps = {
  variant: 'page' | 'panel'
  robots: IRobotInfo[]
  robotsForStats: IRobotInfo[]
  stations: IStation[]
  loadingProgress?: PROGRESS_TYPES
  highlightedRobotId?: number
  onRobotsUpdated: () => void
  onStationsChanged: () => void
  hideDeleteRobots?: boolean
  onExcludeRobot?: (robot: IRobotInfo) => Promise<void> | void
  currentPage?: number
  setCurrentPage?: React.Dispatch<React.SetStateAction<number>>
  totalCount?: number
  pageSize?: number
  onPageSizeChange?: (size: number) => void
  pageInput?: string
  setPageInput?: (val: string) => void
  handlePageJump?: (e: KeyboardEvent<HTMLInputElement>) => void
  panelPage?: number
  panelPageSize?: number
  panelPageInput?: string
  setPanelPage?: React.Dispatch<React.SetStateAction<number>>
  setPanelPageInput?: (val: string) => void
  onPanelPageJump?: (e: KeyboardEvent<HTMLInputElement>) => void
  isActive?: boolean
  emptyTitle?: string
  emptyDescription?: string
}

const AssetsRobotsPanel = ({
  variant,
  robots,
  robotsForStats,
  stations,
  loadingProgress = PROGRESS_TYPES.SUCCESS,
  highlightedRobotId,
  onRobotsUpdated,
  onStationsChanged,
  hideDeleteRobots,
  onExcludeRobot,
  currentPage = 1,
  setCurrentPage,
  totalCount = 0,
  pageSize = 20,
  onPageSizeChange,
  pageInput = '1',
  setPageInput,
  handlePageJump,
  panelPage = 1,
  panelPageSize = 20,
  panelPageInput = '1',
  setPanelPage,
  setPanelPageInput,
  onPanelPageJump,
  isActive = true,
  emptyTitle,
  emptyDescription,
}: AssetsRobotsPanelProps) => {
  const { t } = useTranslation()
  const user = useUser()
  const tableRef = useRef<HTMLDivElement>(null)
  const pageSizeRef = useRef(pageSize)
  pageSizeRef.current = pageSize

  useEffect(() => {
    if (variant !== 'page' || !onPageSizeChange || !isActive) {
      return
    }
    const tableElement = tableRef.current
    if (!tableElement) {
      return
    }

    const getFallbackRowHeight = () => {
      const styles = getComputedStyle(document.documentElement)
      const rowHeightVar = styles.getPropertyValue('--row-height').trim()
      const parsedRowHeight = Number.parseFloat(rowHeightVar)
      if (!Number.isNaN(parsedRowHeight) && parsedRowHeight > 0) {
        return parsedRowHeight
      }
      return 52
    }

    const recalculatePageSize = () => {
      const firstRow = tableElement.firstElementChild as HTMLElement | null
      const rowHeight = firstRow?.getBoundingClientRect().height || getFallbackRowHeight()
      if (rowHeight <= 0) {
        return
      }
      const maxVisibleRows = Math.max(1, Math.floor(tableElement.clientHeight / rowHeight))
      if (maxVisibleRows !== pageSizeRef.current) {
        onPageSizeChange(maxVisibleRows)
      }
    }

    recalculatePageSize()
    const observer = new ResizeObserver(recalculatePageSize)
    observer.observe(tableElement)
    window.addEventListener('resize', recalculatePageSize)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', recalculatePageSize)
    }
  }, [variant, onPageSizeChange, isActive, robotsForStats.length])

  const favoriteRobotIds = useMemo(
    () => new Set(user?.state.favoriteRobotIds ?? []),
    [user?.state.favoriteRobotIds]
  )
  const orderedRobots = useMemo(
    () => (variant === 'panel' ? sortWithFavoritesFirst(robots, favoriteRobotIds) : robots),
    [variant, robots, favoriteRobotIds]
  )
  const visibleRobots =
    variant === 'panel'
      ? orderedRobots.slice((panelPage - 1) * panelPageSize, panelPage * panelPageSize)
      : orderedRobots

  const overallCount = robotsForStats.length
  const onlineCount = robotsForStats.filter(r => r.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE).length
  const activeCount = robotsForStats.filter(
    r => r.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE && hasActiveTaskRuntimeState(r)
  ).length

  if (loadingProgress === PROGRESS_TYPES.WORK) {
    return <Loader />
  }

  if (loadingProgress === PROGRESS_TYPES.ERROR) {
    return (
      <div className={robotStyle.emptyWrapper}>
        <div className={robotStyle.emptyTitle}>{t('common.error')}</div>
      </div>
    )
  }

  return (
    <div className={robotStyle.tableWrapper}>
      <FleetStats overall={overallCount} online={onlineCount} active={activeCount} />
      <div className={robotStyle.tableHeader}>
        <div />
        <div className={clsx(robotStyle.tableHeaderItem, 'text-16-400')}>{t('robots.serial_id')}</div>
        <div className={clsx(robotStyle.tableHeaderItem, 'text-16-400')}>{t('robots.name')}</div>
        <div className={clsx(robotStyle.tableHeaderItem, 'text-16-400')}>{t('robots.battery')}</div>
        <div className={clsx(robotStyle.tableHeaderItemCentered, 'text-16-400')}>{t('robots.mode')}</div>
        <div />
      </div>
      {visibleRobots.length ? (
        <div className={robotStyle.table} ref={variant === 'page' ? tableRef : undefined}>
          {visibleRobots.map(robot => (
            <RobotItem
              key={robot.id}
              robot={robot}
              robots={robotsForStats}
              stations={stations}
              onRobotUpdated={onRobotsUpdated}
              onStationsChanged={onStationsChanged}
              hightlighted={robot.id === highlightedRobotId}
              hideDelete={hideDeleteRobots}
              onExclude={onExcludeRobot}
            />
          ))}
        </div>
      ) : (
        <div className={robotStyle.emptyWrapper}>
          <div className={robotStyle.emptyTitle}>{emptyTitle || t('robots.no_robots_title')}</div>
          <div className={robotStyle.emptyDescription}>
            {emptyDescription || t('robots.no_robots_description')}
          </div>
        </div>
      )}
      {variant === 'page' && totalCount > pageSize && setCurrentPage && setPageInput && handlePageJump && (
        <TablePagination
          page={currentPage}
          totalPages={Math.ceil(totalCount / pageSize) || 1}
          pageInput={pageInput}
          onPageInputChange={setPageInput}
          onPageInputKeyDown={handlePageJump}
          onPageInputBlur={() => setPageInput(currentPage.toString())}
          onPrev={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          onNext={() => setCurrentPage(prev => prev + 1)}
        />
      )}
      {variant === 'panel' && orderedRobots.length > panelPageSize && setPanelPage && setPanelPageInput && (
        <TablePagination
          page={panelPage}
          totalPages={Math.ceil(orderedRobots.length / panelPageSize) || 1}
          pageInput={panelPageInput}
          onPageInputChange={setPanelPageInput}
          onPageInputKeyDown={onPanelPageJump ?? (() => {})}
          onPageInputBlur={() => setPanelPageInput(panelPage.toString())}
          onPrev={() => setPanelPage(prev => Math.max(prev - 1, 1))}
          onNext={() => setPanelPage(prev => prev + 1)}
        />
      )}
    </div>
  )
}

export default AssetsRobotsPanel
